import type { GameState } from "./types.ts";
import { XP } from "../config/constants.ts";

export interface XpBreakdown {
  base: number;
  moveBonus: number;
  timeBonus: number;
  undoPenalty: number;
  hintPenalty: number;
  modeMultiplier: number;
  firstDailyMultiplier: number;
  streakBonus: number;
  total: number;
}

export interface XpContext {
  isFirstDailyWin: boolean;
  currentStreakDays: number;
}

export function computeWinXp(s: GameState, ctx: XpContext): XpBreakdown {
  if (!s.won) {
    return zeroBreakdown();
  }

  const base = XP.WIN_BASE;

  const movesOver = Math.max(0, s.moves - XP.MOVE_BONUS_REF);
  const moveBonus = Math.max(
    0,
    XP.MOVE_BONUS_MAX - movesOver * XP.MOVE_BONUS_FALLOFF_PER_MOVE,
  );

  const elapsedSec = Math.max(1, Math.round(s.elapsedMs / 1000));
  let timeBonus = 0;
  for (let i = 0; i < XP.TIME_BONUS_THRESHOLDS_SEC.length; i++) {
    if (elapsedSec <= XP.TIME_BONUS_THRESHOLDS_SEC[i]!) {
      timeBonus = XP.TIME_BONUS_VALUES[i]!;
      break;
    }
  }

  const undoPenalty = s.undosUsed * XP.UNDO_PENALTY;
  const hintPenalty = s.hintsUsed * XP.HINT_PENALTY;

  const modeMultiplier = s.drawMode === "three" ? XP.MULTIPLIER_3CARD : 1;
  const firstDailyMultiplier = ctx.isFirstDailyWin ? XP.FIRST_DAILY_MULTIPLIER : 1;
  const streakBonus = Math.min(
    XP.STREAK_BONUS_MAX,
    Math.max(0, ctx.currentStreakDays - 1) * XP.STREAK_BONUS_PER_DAY,
  );

  const subtotal = Math.max(
    0,
    base + moveBonus + timeBonus - undoPenalty - hintPenalty,
  );
  const withMultipliers = Math.round(
    subtotal * modeMultiplier * firstDailyMultiplier + streakBonus,
  );

  return {
    base,
    moveBonus,
    timeBonus,
    undoPenalty,
    hintPenalty,
    modeMultiplier,
    firstDailyMultiplier,
    streakBonus,
    total: withMultipliers,
  };
}

function zeroBreakdown(): XpBreakdown {
  return {
    base: 0,
    moveBonus: 0,
    timeBonus: 0,
    undoPenalty: 0,
    hintPenalty: 0,
    modeMultiplier: 1,
    firstDailyMultiplier: 1,
    streakBonus: 0,
    total: 0,
  };
}
